import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import saleService from '../../services/saleService';

// Async thunks
export const getSales = createAsyncThunk(
  'sales/getSales',
  async (_, { rejectWithValue }) => {
    try {
      const response = await saleService.getSales();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sales');
    }
  }
);

export const getActiveSales = createAsyncThunk(
  'sales/getActiveSales',
  async (_, { rejectWithValue }) => {
    try {
      const response = await saleService.getActiveSales();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active sales');
    }
  }
);

export const getSale = createAsyncThunk(
  'sales/getSale',
  async (id, { rejectWithValue }) => {
    try {
      const response = await saleService.getSale(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch sale');
    }
  }
);

export const createSale = createAsyncThunk(
  'sales/createSale',
  async (saleData, { rejectWithValue }) => {
    try {
      const response = await saleService.createSale(saleData);
      return response.data;
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        // Handle validation errors
        const errorMessages = errorData.errors.map(err => 
          typeof err === 'string' ? err : err.msg || err.message || 'Validation error'
        ).join(', ');
        return rejectWithValue(errorMessages || errorData.message || 'Failed to create sale');
      }
      return rejectWithValue(errorData?.message || 'Failed to create sale');
    }
  }
);

export const updateSale = createAsyncThunk(
  'sales/updateSale',
  async ({ id, saleData }, { rejectWithValue }) => {
    try {
      const response = await saleService.updateSale(id, saleData);
      return response.data;
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.errors && Array.isArray(errorData.errors)) {
        // Handle validation errors
        const errorMessages = errorData.errors.map(err => 
          typeof err === 'string' ? err : err.msg || err.message || 'Validation error'
        ).join(', ');
        return rejectWithValue(errorMessages || errorData.message || 'Failed to update sale');
      }
      return rejectWithValue(errorData?.message || 'Failed to update sale');
    }
  }
);

export const deleteSale = createAsyncThunk(
  'sales/deleteSale',
  async (id, { rejectWithValue }) => {
    try {
      await saleService.deleteSale(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete sale');
    }
  }
);

const initialState = {
  sales: [],
  activeSales: [],
  currentSale: null,
  isLoading: false,
  error: null,
  success: null,
};

const saleSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearCurrentSale: (state) => {
      state.currentSale = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Sales
      .addCase(getSales.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSales.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sales = Array.isArray(action.payload) ? action.payload : (action.payload.sales || []);
      })
      .addCase(getSales.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Active Sales
      .addCase(getActiveSales.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getActiveSales.fulfilled, (state, action) => {
        state.isLoading = false;
        state.activeSales = Array.isArray(action.payload) ? action.payload : (action.payload.activeSales || []);
      })
      .addCase(getActiveSales.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Single Sale
      .addCase(getSale.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSale.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSale = action.payload.sale || action.payload;
      })
      .addCase(getSale.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create Sale
      .addCase(createSale.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSale.fulfilled, (state, action) => {
        state.isLoading = false;
        const newSale = action.payload.sale || action.payload;
        state.sales.push(newSale);
        state.success = 'Sale created successfully';
      })
      .addCase(createSale.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Sale
      .addCase(updateSale.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSale.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedSale = action.payload.sale || action.payload;
        const index = state.sales.findIndex(sale => sale._id === updatedSale._id);
        if (index !== -1) {
          state.sales[index] = updatedSale;
        }
        state.success = 'Sale updated successfully';
      })
      .addCase(updateSale.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Sale
      .addCase(deleteSale.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSale.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sales = state.sales.filter(sale => sale._id !== action.payload);
        state.success = 'Sale deleted successfully';
      })
      .addCase(deleteSale.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccess, clearCurrentSale } = saleSlice.actions;

// Selectors
export const selectSales = (state) => state.sales.sales;
export const selectActiveSales = (state) => state.sales.activeSales;
export const selectCurrentSale = (state) => state.sales.currentSale;
export const selectSalesLoading = (state) => state.sales.isLoading;
export const selectSalesError = (state) => state.sales.error;
export const selectSalesSuccess = (state) => state.sales.success;

export default saleSlice.reducer;
